import React, { useState, useEffect } from 'react'
import { Box, Text, Flex, Group, Button, Skeleton, CopyButton, Tooltip, ThemeIcon } from '@mantine/core'
import { generateUrlPublicSourcingRequest, generateShortUrlSourcingRequest } from '../../../services/sourcing-request'
import { IconCheck, IconCopy, IconWindowMaximize } from '@tabler/icons-react'

const defaultVal = {
  id: null,
  signatureUrl: null,
  shortLinkUrl: null
}

const ShareLinkProductRequest = ({ dataSourcingRequest, onCloseModal }) => {
  const [detailUrl, setDetailUrl] = useState(defaultVal)
  const [loadingSignature, setLoadingSignature] = useState(false)
  const [loadingShortLink, setLoadingShortLink] = useState(false)
  const apiUrl = process.env.REACT_APP_SERVER_API_URL

  const handleGenerateAllUrl = async (id) => {
    await generatePublicUrl(id)
    await generateShortUrl(id)
  }

  const generatePublicUrl = async (idSourcing) => {
    setLoadingSignature(true)
    try {
      const response = await generateUrlPublicSourcingRequest(idSourcing)
      if (response.success) {
        const codeSignature = response.message
        const getSignatureUrl = codeSignature.includes('Successful create temporary url with signature:')
            ? codeSignature.replace('Successful create temporary url with signature:', '')
            : codeSignature.replace('This request quote already has a temporary url with signature:', '')
        
        handleChangeDetail('signatureUrl', getSignatureUrl)
        
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingSignature(false)
    }
  }

  const generateShortUrl = async (idSourcing) => {
    setLoadingShortLink(true)
    try {
      const response = await generateShortUrlSourcingRequest(idSourcing)
      if (response.success) {
        const shortUrl = response.data.short_url
        handleChangeDetail('shortLinkUrl', shortUrl)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingShortLink(false)
    }
  }

  useEffect(() => {
    if (dataSourcingRequest !== null) {
      handleSetDetail(dataSourcingRequest)
    }
  }, [dataSourcingRequest])

  const handleSetDetail = (val) => {
    const detail = {
      id: val.id,
      signatureUrl: val.signature ? val.signature : null,
      shortLinkUrl: val.short_url ? val.short_url : null
    }
    setDetailUrl(detail)
  }

  const handleChangeDetail = (name, val) => {
    setDetailUrl((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const handleApiUrl = (api) => {
    if (api.includes('api2dev')) {
      return 'https://app-dev.asiacommerce.net'
    } else {
      return 'https://app.asiacommerce.net'
    }
  }

  const openNewTab = (url) => {
    window.open(`${url}`, '_blank')
  }

  const mappingLink = (data) => {
    if (data.signatureUrl === null && data.shortLinkUrl === null) {
      return (
        <Box ta='center' my={10}>
          <Button size='xs' variant='transparent' color='blue.9' onClick={() => handleGenerateAllUrl(data.id)}>Click to Generate URL</Button>
        </Box>
      )
    } else {
      const mappingSignature = (signatureUrl) => {
        if (signatureUrl !== null) {
          return (
            <Flex gap={4} align='center'>
              <Text fz={12} c='blue.9'>{`${handleApiUrl(apiUrl)}/request/${detailUrl.id}/${signatureUrl}`}</Text>
              <CopyButton value={`${handleApiUrl(apiUrl)}/request/${detailUrl.id}/${signatureUrl}`} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                    <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                      {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                    </ThemeIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <Tooltip label='Open Link' withArrow position="top">
                <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${handleApiUrl(apiUrl)}/request/${detailUrl.id}/${signatureUrl}`)}>
                  <IconWindowMaximize size={12} />
                </ThemeIcon>
              </Tooltip>
            </Flex>
          )
        }
      }

      const mappingShortLinkUrl = (dataUrl) => {
        if (dataUrl.signatureUrl !== null && dataUrl.shortLinkUrl === null) {
          return (
            <Box ta='center' my={10}>
              <Button size='xs' variant='transparent' color='blue.9' onClick={() => generateShortUrl(dataUrl.id)}>Click to Generate Short Link</Button>
            </Box>
          )
        } else {
          return (
            <Flex gap={4} align='center'>
              <Text fz={12} c='blue.9'>{`${dataUrl.shortLinkUrl}`}</Text>
              <CopyButton value={`${dataUrl.shortLinkUrl}`} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                    <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                      {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                    </ThemeIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <Tooltip label='Open Link' withArrow position="top">
                <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${dataUrl.shortLinkUrl}`)}>
                  <IconWindowMaximize size={12} />
                </ThemeIcon>
              </Tooltip>
            </Flex>
          )
        }
      }

      return (
        <Box>
          {loadingSignature ? <Box w={100}><Skeleton height={14} radius="xl" /></Box> : mappingSignature(data.signatureUrl)}
          <Box mt={8}>
            <Text fz={12} fw='500' c='gray.6' mb={2}>Short Link</Text>
            {loadingShortLink ? <Box w={100}><Skeleton height={14} radius="xl" /></Box> : mappingShortLinkUrl(data)}
          </Box>
        </Box>
      )
    }
  }

  return (
    <Box>
      <Box mb='lg'>
        <Text fz={14} fw='600' mb={2}>Restricted URL</Text>
        <Text fz={12} fw='500' c='gray.6' mb={1}>Need Login to App</Text>
        <Flex gap={4} align='center'>
          <Text fz={12} c='blue.9'>{`${handleApiUrl(apiUrl)}/request/${detailUrl.id}`}</Text>
          <CopyButton value={`${handleApiUrl(apiUrl)}/request/${detailUrl.id}`} timeout={2000}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ThemeIcon>
              </Tooltip>
            )}
          </CopyButton>
          <Tooltip label='Open Link' withArrow position="top">
            <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${handleApiUrl(apiUrl)}/request/${detailUrl.id}`)}>
              <IconWindowMaximize size={12} />
            </ThemeIcon>
          </Tooltip>
        </Flex>
      </Box>
      <Box mb='lg'>
        <Text fz={14} fw='600' mb={2}>Public URL (With Limited Time)</Text>
        <Text fz={12} fw='500' c='gray.6' mb={1}>The generated link expired after 365 days. No need to login</Text>
        <Box>
          {mappingLink(detailUrl)}
        </Box>
      </Box>
      <Box mt={20}>
        <Flex justify='flex-end'>
          <Group>
            <Button size='xs' variant="filled" onClick={onCloseModal}>Close</Button>
          </Group>
        </Flex>
      </Box>
    </Box>
  )
}

export default ShareLinkProductRequest